import React, { useState, useRef } from 'react';
import { t } from '@lingui/core/macro';
import './VideoSettings.css';
import { Select, MenuItem, SelectChangeEvent } from "@mui/material";
import ColorPicker from './ColorPicker';

// Render format types - video formats call /new-render, image formats call /export-png
export type VideoFormat = 
  | 'H264' | 'H265' 
  | 'ProRes_422_Proxy' | 'ProRes_422_LT' | 'ProRes_422' | 'ProRes_422_HQ' 
  | 'ProRes_4444' | 'ProRes_4444_XQ';

export type ImageFormat = 'PDF' | 'PNG' | 'TIFF';

export type RenderFormat = VideoFormat | ImageFormat;

export const VIDEO_FORMATS: VideoFormat[] = [
  'H264', 'H265', 
  'ProRes_422_Proxy', 'ProRes_422_LT', 'ProRes_422', 'ProRes_422_HQ', 
  'ProRes_4444', 'ProRes_4444_XQ'
];

export const IMAGE_FORMATS: ImageFormat[] = ['PDF', 'PNG', 'TIFF'];

export function isVideoFormat(format: RenderFormat): format is VideoFormat {
  return VIDEO_FORMATS.includes(format as VideoFormat);
}

export function isImageFormat(format: RenderFormat): format is ImageFormat {
  return IMAGE_FORMATS.includes(format as ImageFormat);
}

export interface VideoSettings {
  background_color: string;
  fps_num: number;
  resolution: string;
  transparent: boolean;
  video_time: number;
  aspect_ratio: string;
  video_format: RenderFormat;
  fonts?: Record<string, string>;
  logos?: Record<string, { url: string; isEnabled: boolean }>;
}

export const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
  background_color: '#000000',
  fps_num: 30,
  resolution: "HD",
  transparent: false,
  video_time: 0,
  aspect_ratio: '16:9',
  video_format: 'H264',
  fonts: {},
  logos: {},
};

const RESOLUTION_WIDTHS: Record<string, number> = {
  "HD": 1280,
  "2K": 2048,
  "UHD": 3840,
  "4K": 4096,
};

function calculateVideoHeight(settings: VideoSettings): number {
  const width = RESOLUTION_WIDTHS[settings.resolution] ?? 1920;
  const [w, h] = settings.aspect_ratio.split(":").map(Number);
  if (w && h) return Math.round(width * (h / w));
  return 1080;
}

function toDisplayVideoTime(settings: VideoSettings, mode: 'total' | 'pixels'): string {
  if (mode === 'pixels') {
    const height = calculateVideoHeight(settings);
    if (!settings.video_time || settings.video_time <= 0) return "";
    const pixelsPerSecond = height / settings.video_time;
    return Number(pixelsPerSecond.toFixed(2)).toString();
  }

  return settings.video_time ? settings.video_time.toString() : "";
}

interface VideoSettingsPanelProps {
  settings: VideoSettings;
  onSettingsChange: (settings: VideoSettings) => void | Promise<void>;
  showTitle?: boolean;
}

export const VideoSettingsPanel: React.FC<VideoSettingsPanelProps> = ({ 
  settings, 
  onSettingsChange,
  showTitle = true,
}) => {
  const [timeMode, setTimeMode] = useState<'total' | 'pixels'>('total');
  const [videoTimeInput, setVideoTimeInput] = useState(() => 
    toDisplayVideoTime(settings, timeMode)
  );
  const isEditingRef = useRef<boolean>(false);

  // Sync input with external settings when not actively editing
  const nextDisplay = toDisplayVideoTime(settings, timeMode);
  if (!isEditingRef.current && videoTimeInput !== nextDisplay) {
    setVideoTimeInput(nextDisplay);
  }

  const handleSettingsChange = (updates: Partial<VideoSettings>) => {
    const newSettings = { ...settings, ...updates };
    const result = onSettingsChange(newSettings);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onSettingsChange:', error);
      });
    }
  };

  const handleFpsChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ fps_num: parseInt(e.target.value, 10) });
  };

  const handleResolutionChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ resolution: e.target.value });
  };

  const handleAspectRatioChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ aspect_ratio: e.target.value });
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setVideoTimeInput(raw);

    if (raw === "" || raw === null) {
      handleSettingsChange({ video_time: 0 });
      return;
    }

    const value = parseFloat(raw);
    if (isNaN(value) || value <= 0) {
      handleSettingsChange({ video_time: 0 });
      return;
    }

    if (timeMode === 'pixels') {
      const height = calculateVideoHeight(settings);
      const seconds = height > 0 ? Number((height / value).toFixed(2)) : 0;
      handleSettingsChange({ video_time: seconds });
    } else {
      handleSettingsChange({ video_time: value });
    }
  };

  const handleTransparentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSettingsChange({ transparent: e.target.checked });
  };

  const handleTimeModeChange = (e: SelectChangeEvent<string>) => {
    const newMode = e.target.value as 'total' | 'pixels';
    setTimeMode(newMode);
    const nextDisplay = toDisplayVideoTime(settings, newMode);
    setVideoTimeInput(nextDisplay);
  };

  return (
    <div className="video-settings-panel">
      {showTitle ? <h2>{t`Video Settings`}</h2> : null}
      <div className="video-settings-group">
        {/* Background Color */}
        <div className="setting-item">
          <label>{t`Background Color`}</label>
          <div className="input-wrapper">
            {settings.transparent ? (
              <div
                className="transparent-swatch"
                title="Transparent background"
              >
                T
              </div>
            ) : (
              <ColorPicker
                value={settings.background_color}
                onChange={(color) => handleSettingsChange({ background_color: color })}
              />
            )}
            <label onClick={e => e.preventDefault()}>
              {t`Transparent`}:
              <input type="checkbox" checked={settings.transparent} onChange={handleTransparentChange} />
            </label>
          </div>
        </div>

        {/* FPS */}
        <div className="form-group" style={{ width: '140px' }}>
          <label>{t`FPS`}</label>
          <div className="input-wrapper">
            <Select
              value={settings.fps_num.toString()}
              onChange={handleFpsChange}
              style={{ width: '92px', height: '34px', fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="24">24</MenuItem>
              <MenuItem value="30">30</MenuItem>
              <MenuItem value="60">60</MenuItem>
              <MenuItem value="90">90</MenuItem>
              <MenuItem value="120">120</MenuItem>
            </Select>
          </div>
        </div>

        {/* Resolution */}
        <div className="form-group" style={{ width: '320px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {t`Resolution`}
            <div className="tooltip-wrapper">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text" style={{ paddingBottom: '1px' }}>
                {t`This setting determines the size of the video. Adjust font size accordingly.`}
              </span>
            </div>
          </label>
          <div className="input-wrapper">
            <Select
              value={settings.resolution}
              onChange={handleResolutionChange}
              style={{ width: '80px', height: '34px', fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="HD">HD</MenuItem>
              <MenuItem value="2K">2K</MenuItem>
              <MenuItem value="UHD">UHD</MenuItem>
              <MenuItem value="4K">4K</MenuItem>
            </Select>
          </div>
        </div>

        {/* Aspect Ratio */}
        <div className="form-group" style={{ width: '320px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {t`Aspect Ratio`}
            <div className="tooltip-wrapper" style={{ display: 'flex', alignItems: 'center', height: 'fit-content' }}>
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text-ratio">{t`This setting greatly affects the video size.`}</span>
            </div>
          </label>
          <div className="input-wrapper">
            <Select
              value={settings.aspect_ratio}
              onChange={handleAspectRatioChange}
              style={{ width: '200px', height: '36px', fontSize: '16px', lineHeight: '36px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="4:3">4:3 / 1.33</MenuItem>
              <MenuItem value="16:9">16:9 / 1.78</MenuItem>
              <MenuItem value="37:20">37:20 / 1.85</MenuItem>
              <MenuItem value="2:1">2:1 / 2.00</MenuItem>
              <MenuItem value="239:100">239:100 / 2.39</MenuItem>
            </Select>
          </div>
        </div>

        {/* Video Time */}
        <div className="setting-item">
          <label style={{ fontWeight: 500 }}>
            {timeMode === 'total' ? t`Video Time (seconds)` : t`Pixels per second`}
          </label>
          <div className="video-time-controls">
            <input
              id="time-setting"
              type="number"
              step="1"
              value={videoTimeInput}
              onChange={handleTimeChange}
              onFocus={() => { isEditingRef.current = true; }}
              onBlur={() => {
                isEditingRef.current = false;
                const raw = videoTimeInput.trim();
                const num = parseFloat(raw);
                handleSettingsChange({ video_time: isNaN(num) ? 0 : num });
                setVideoTimeInput(isNaN(num) ? "" : num.toString());
              }}
              className={videoTimeInput === "" ? "input-empty video-time-input" : "video-time-input"}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select
                value={timeMode}
                onChange={handleTimeModeChange}
                style={{ width: '110px', height: '34px', fontSize: '14px' }}
                sx={{
                  '.MuiSelect-select': { textAlign: 'left', paddingLeft: '8px' },
                  '.MuiSelect-icon': { color: '#007CB2' }
                }}
                MenuProps={{ PaperProps: { style: { fontSize: 13 } } }}
              >
                <MenuItem value="total">{t`Total Time`}</MenuItem>
                <MenuItem value="pixels">{t`Pixels/sec`}</MenuItem>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
